import { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveSheetPagination } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheetPagination';
import { useLoadPDF } from 'src/components/viewers/SheetsViewerV2/hooks/useLoadPDF';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { MappedPDFData } from 'src/components/viewers/SheetsViewerV2/mappers/mapPDFData';
import { getSheetUrl } from 'src/components/viewers/SheetsViewerV2/utils/s3';
import { useLocalStorage } from 'src/hooks/local-storage';
import { useS3Client } from 'src/hooks/s3';
import { Sheet, useSheets } from 'src/hooks/sheets';
import { useUserTenant } from 'src/services/auth-info';
import { useProjectHistory } from 'src/components/project/project-history/hooks/useProjectHistory';
import { getHistoricalSheets } from 'src/components/project/project-history/hooks/useDiffing';

type Props = { projectId: string; aggregatedView: AggregatedView | null };

export type ActiveSheetData = {
  id: string;
  page: number;
  projectId: string;
} & MappedPDFData;
export type ActiveSheet = {
  isLoading: boolean;
  data?: ActiveSheetData;
  error?: Error;
};
const InitActiveSheetState = { isLoading: true };

export const useActiveSheet = ({ projectId, aggregatedView }: Props) => {
  const debounceRef = useRef<number>();
  const s3Client = useS3Client(projectId);
  const { sheets } = useSheets(projectId);
  const { tenant } = useUserTenant();
  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();
  const { loadPDF, unloadPDF } = useLoadPDF(aggregatedView);
  const [activeSheetId, changeActiveSheet] = useLocalStorage<string | null>(
    `${projectId}-active-sheet-id`,
    null
  );
  const [activeSheet, setActiveSheet] =
    useState<ActiveSheet>(InitActiveSheetState);
  const pagination = useActiveSheetPagination(activeSheet.data);

  const effectiveSheets: Sheet[] = useMemo(() => {
    // In history mode, build historical sheets and enrich with s3Key
    if (isHistoryDrawerOpen && referenceState && diff && tenant?.group) {
      const { sheets: hist } = getHistoricalSheets(
        referenceState,
        diff,
        projectId
      );
      const enriched = hist
        .filter((s) => typeof s.version === 'number' && !!s.filename)
        .map((s) => ({
          ...s,
          s3Key: `${tenant.group}/project/${projectId}/sheets/${s.version}/${s.filename}`,
        }));
      return enriched as Sheet[];
    }
    return sheets || [];
  }, [
    diff,
    isHistoryDrawerOpen,
    projectId,
    referenceState,
    sheets,
    tenant?.group,
  ]);

  // In history mode, if the current active sheet doesn't exist, pick by same name or first
  useEffect(() => {
    if (!isHistoryDrawerOpen) return;
    if (!effectiveSheets || effectiveSheets.length === 0) return;

    const existsInHistorical = Boolean(
      activeSheetId && effectiveSheets.some((s) => s.id === activeSheetId)
    );
    if (existsInHistorical) return;

    const resolveFallbackId = () => {
      const currentName = activeSheetId
        ? [...(sheets || []), ...effectiveSheets].find(
            (s) => s.id === activeSheetId
          )?.name
        : undefined;
      if (currentName) {
        const sameName = effectiveSheets
          .filter((s) => s.name === currentName)
          .sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
        if (sameName.length > 0) return sameName[0].id;
      }
      return effectiveSheets[0]?.id ?? null;
    };

    const fallbackId = resolveFallbackId();
    if (fallbackId) changeActiveSheet(fallbackId);
  }, [
    isHistoryDrawerOpen,
    effectiveSheets,
    activeSheetId,
    sheets,
    changeActiveSheet,
  ]);

  // Handle the case when there are no sheets or no activeSheetId
  useEffect(() => {
    // This effect could have probably been combined with the one below it
    // but no time to test edge cases now so will leave it as is
    if (!effectiveSheets || effectiveSheets.length === 0) {
      // unload the pdf model from the viewer
      unloadPDF();
      setActiveSheet({ isLoading: false });
    }
    // Added the activeSheetId so that if we have a rapid opening and closing of the history drawer,
    // we can be able to catch loaded PDFs since after a successful load, we set the active sheet
  }, [effectiveSheets, activeSheetId, unloadPDF]);

  useEffect(() => {
    if (!effectiveSheets || !activeSheetId) return;
    // reset pagination after changing a sheet model
    if (activeSheetId !== activeSheet?.data?.id) pagination.setPageNumber(1);
    // used debounce effect to prevent doubled loading of the sheet
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(() => {
      setActiveSheet((prevState) => ({ ...prevState, isLoading: true }));
      getSheetUrl(s3Client, effectiveSheets, activeSheetId)
        .then((signedUrl) =>
          signedUrl
            ? loadPDF(
                projectId,
                activeSheetId,
                signedUrl,
                pagination.pageNumber
              )
            : Promise.reject(new Error('Could not load pdf'))
        )
        .then((data) => {
          setActiveSheet({ data, isLoading: false });
        })
        .catch((error) => {
          setActiveSheet({ error, isLoading: false });
        });
    }, 50);
  }, [
    activeSheetId,
    aggregatedView,
    effectiveSheets,
    pagination.pageNumber,
    s3Client,
    loadPDF,
  ]);

  return {
    sheets: effectiveSheets,
    activeSheet,
    activeSheetId,
    pagination,
    setPageNumber: pagination.setPageNumber,
    changeActiveSheet,
  };
};
